/**
 * 阶段指标计算模块
 */
import { ScenarioData, StrategyPhase, PhaseMetrics } from '../types';
import { calculateEffectivePriceForScenario, calculateCouponCost } from './discounts';
import { calculateWeeklyMetrics } from './weeklyMetrics';

/**
 * 广告推演模型的计算逻辑
 */
export const calculateMetrics = (
    data: ScenarioData,
    price: number,
    profitMarginPreAd: number,
    landedCost: number
) => {
    const totalUnits = data.dailyUnits * data.days;

    // 计算实际售价（考虑 Prime 和 Coupon）
    const effectivePrice = calculateEffectivePriceForScenario(price, data);
    const sales = effectivePrice * totalUnits;

    // 用户公式：广告预算 = 销量 * 广告占比 / 转化率 * cpc
    // 这里的 data.tacosTarget 被理解为 广告出单占比
    const adContributionPercent = data.tacosTarget / 100;
    const cvrPercent = data.cvr / 100;

    // 广告预算计算
    const adBudget = cvrPercent > 0 ? (totalUnits * adContributionPercent / cvrPercent) * data.cpc : 0;

    const cpo = data.cvr > 0 ? data.cpc / (data.cvr / 100) : 0;

    // TACOS (Total ACOS) = 广告费 / 总销售额
    const tacos = sales > 0 ? (adBudget / sales) * 100 : 0;

    // ACOS (Advertising ACOS) = CPC / (单价 * 转化率)
    const acos = (price > 0 && cvrPercent > 0) ? (data.cpc / (price * cvrPercent)) * 100 : 0;

    // Coupon 成本计算
    const couponCost = data.enableCoupon ? calculateCouponCost(sales, data.couponUsageRate || 0) : 0;

    const grossProfit = sales * (profitMarginPreAd / 100);
    const netProfit = grossProfit - adBudget - couponCost;
    const netMargin = sales > 0 ? (netProfit / sales) * 100 : 0;
    const productCost = landedCost * totalUnits;
    const totalInvest = productCost + adBudget + couponCost;
    const roi = totalInvest > 0 ? (netProfit / totalInvest) * 100 : 0;

    return {
        totalSales: sales,
        effectivePrice,      // 实际售价
        adBudget,
        couponCost,          // Coupon 成本
        cpo,
        tacos,
        acos,
        netProfit,
        netMargin,
        roi
    };
};

/**
 * 计算分阶段运营策略的各项指标（重构版v2）
 */
export const calculatePhaseMetrics = (
    phase: StrategyPhase,
    landedCost: number,
    fbaFee: number,
    referralRate: number,
    storageFee: number
): PhaseMetrics => {
    // 计算每周指标
    const weeklyMetrics = phase.weeks.map(week =>
        calculateWeeklyMetrics(week, landedCost, fbaFee, referralRate, storageFee)
    );

    // 汇总阶段指标
    const totalSales = weeklyMetrics.reduce((sum, w) => sum + w.sales, 0);
    const totalNetProfit = weeklyMetrics.reduce((sum, w) => sum + w.netProfit, 0);
    const averageNetMargin = totalSales > 0 ? (totalNetProfit / totalSales) * 100 : 0;
    const netMargin = averageNetMargin; // netMargin 和 averageNetMargin 在阶段层面是一样的

    // 计算总成本用于 ROI
    const totalCouponCost = weeklyMetrics.reduce((sum, w) => sum + w.couponCost, 0);
    const totalAdCost = weeklyMetrics.reduce((sum, w) => sum + w.adCost, 0);
    const totalUnits = phase.weeks.reduce((sum, w) => {
        const daysInWeek = w.weekNumber === 13 ? 6 : 7;
        return sum + (w.dailyUnits * daysInWeek);
    }, 0);
    const totalProductCost = landedCost * totalUnits;
    const totalCost = totalProductCost + totalCouponCost + totalAdCost;
    const totalROI = totalCost > 0 ? (totalNetProfit / totalCost) * 100 : 0;

    return {
        totalSales,
        totalNetProfit,
        totalUnits,
        totalAdCost,
        netMargin,
        averageNetMargin,
        totalROI,
        weeklyMetrics
    };
};
